/* eslint-disable @typescript-eslint/no-explicit-any */
import { isAdvertisingRejected } from 'src/utils/storedCookieConsent'
import { isInternalTraffic } from './internal'

declare global {
  interface Window {
    oaiq?: ((...args: any[]) => void) & { q?: any[] }
  }
}

/**
 * OpenAI / ChatGPT Ads pixel (`oaiq`) — bet B8 (MARKETING.md §8, launched 2026-08-21).
 * Loader in `constants/scripts.ts` → mounted by `components/Scripts.tsx` (prod only).
 * Official taxonomy: https://developers.openai.com/ads/measurement-pixel
 *
 * ─── CALL SHAPE (this is what the first version got wrong) ─────────────────
 *   oaiq('measure', <event>, <data>, <options>)
 * `event_id` and `custom_event_name` belong in the **4th (options) argument**,
 * NEVER inside `data`. Each data shape accepts a CLOSED set of properties, so
 * an `event_id` smuggled into `data` is rejected as "Invalid event properties"
 * — which is exactly the pixel warning seen on 2026-08-21 (1 invalid event).
 *
 * ─── THE 10 STANDARD EVENTS AND THEIR REQUIRED SHAPES ──────────────────────
 *   contents        → page_viewed · contents_viewed · items_added ·
 *                     checkout_started · order_created
 *                     { type, amount?, currency?, contents?: [{ id, name,
 *                       content_type, quantity, amount, currency }] }
 *   customer_action → lead_created · registration_completed ·
 *                     appointment_scheduled      { type, amount?, currency? }
 *   plan_enrollment → subscription_created · trial_started
 *                                     { type, plan_id?, amount?, currency? }
 * Only documented fields are allowed inside `contents[]`.
 *
 * ─── ⚠️ SENDING ≠ OPTIMIZING (the guardrail that matters) ──────────────────
 * We fan out the full standard set so the platform accumulates data and we can
 * CHOOSE a conversion later — the same pattern as Meta/GA4. That is NOT licence
 * to optimize on soft events. The campaign optimizes on `lead_created` ONLY.
 * Pointing a bidder at browse/intent volume is precisely what produced the
 * storage_secondary + PMax-Display over-attribution mess (MARKETING.md §2), and
 * root CLAUDE.md already forbids the Meta equivalent ("never bare `Lead` as an
 * optimization goal"). Add events here freely; change the campaign's conversion
 * event only with a written decision.
 *
 * ─── ⚠️ `amount` IS AN INTEGER → MINOR UNITS (centavos) ────────────────────
 * The schema types `amount` as an integer, so a MX$599 rent is `59900`, not
 * `599`. Inferred, not stated outright in the docs — if the Ads UI ever shows
 * revenue 100× off, this is the line to change (`toMinorUnits`). Low blast
 * radius today: the platform optimizes on conversion COUNT, not value.
 */

/** Standard events keyed to their required data shape. */
type ContentsEvent =
  | 'page_viewed'
  | 'contents_viewed'
  | 'items_added'
  | 'checkout_started'
  | 'order_created'
type CustomerActionEvent =
  | 'lead_created'
  | 'registration_completed'
  | 'appointment_scheduled'

/** Single entry of a `contents[]` array — documented fields ONLY. */
interface OaiqContent {
  id?: string
  name?: string
  content_type?: string
  quantity?: number
  amount?: number
  currency?: string
}

const CURRENCY = 'MXN'

/** MX$599.00 → 59900. `amount` is typed integer, so minor units. */
const toMinorUnits = (value?: number | null): number | undefined =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.round(value * 100)
    : undefined

const getOaiq = (): Window['oaiq'] | undefined => {
  if (isInternalTraffic() || isAdvertisingRejected()) return undefined
  if (typeof window === 'undefined') return undefined
  return window.oaiq
}

/** Drop undefined keys — the validator rejects malformed values, not absence. */
const compact = <T extends Record<string, any>>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null)
  ) as T

/** `event_id` lives in the OPTIONS arg — enables browser↔server dedup later. */
const send = (
  event: string,
  data: Record<string, any>,
  eventId?: string
): void => {
  const oaiq = getOaiq()
  if (!oaiq) return
  const options = eventId ? { event_id: eventId } : undefined
  if (options) oaiq('measure', event, compact(data), options)
  else oaiq('measure', event, compact(data))
}

interface ContentsPayload {
  amount?: number | null
  contents?: OaiqContent[]
  eventId?: string
}

/** Fire a `contents`-shaped standard event. */
const contents = (event: ContentsEvent, payload: ContentsPayload = {}): void =>
  send(
    event,
    {
      type: 'contents',
      amount: toMinorUnits(payload.amount),
      currency: payload.amount != null ? CURRENCY : undefined,
      contents: payload.contents?.length ? payload.contents : undefined
    },
    payload.eventId
  )

/** Fire a `customer_action`-shaped standard event. */
const customerAction = (
  event: CustomerActionEvent,
  payload: { amount?: number | null; eventId?: string } = {}
): void =>
  send(
    event,
    {
      type: 'customer_action',
      amount: toMinorUnits(payload.amount),
      currency: payload.amount != null ? CURRENCY : undefined
    },
    payload.eventId
  )

/**
 * Build a one-item `contents[]` from a SpotMe listing/config.
 * `content_type` carries our vertical (storage / parking / commercial) so the
 * platform can learn the mix without us inventing undocumented fields.
 */
const listingContent = (input: {
  id?: string
  name?: string
  vertical?: string
  price?: number | null
  quantity?: number
}): OaiqContent[] => [
  {
    id: input.id,
    name: input.name,
    content_type: input.vertical,
    quantity: input.quantity ?? 1,
    amount: toMinorUnits(input.price),
    currency: input.price != null ? CURRENCY : undefined
  }
]

export default {
  contents,
  customerAction,
  listingContent,
  /** Convenience: the campaign's optimization event. */
  leadCreated: (eventId?: string, amount?: number | null) =>
    customerAction('lead_created', { eventId, amount })
}
